/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Collapsed search affordance — the header shows a TRIGGER, and the search field
 * only enters the DOM once it is opened.
 *
 * Selected by `HEADER_COMPOSITION[brand].desktopSearch === 'left-panel-trigger'`
 * (lib/brand/header-composition.ts); today that is BG only. This is not a
 * styling variant of the inline field — live BG's header contains no `<input>`
 * at all:
 *
 *   .reference/captures/bg-home-d-1280/App.tsx:75-78
 *     <div role="button" aria-haspopup="true" aria-label="Open search panel">
 *       <i … 14x14 magnifier …/>
 *       <span …>Search</span>
 *     </div>
 *
 * and the field appears only in the opened panel
 * (.reference/captures/bg-searchbar-m-375/App.tsx:114-115 — a full-width
 * `<input type="text">` beside an `<input type="reset" aria-label="Close
 * search">`).
 *
 * DELIBERATE DIVERGENCE FROM LIVE, flagged not rationalised: live uses a
 * `<div role="button">`. This renders a real `<button type="button">` so it is
 * keyboard-operable and focusable without extra wiring — the accessible
 * behaviour live only approximates. `aria-expanded`/`aria-controls` describe the
 * disclosure to assistive tech; live carries `aria-haspopup` instead, which is
 * wrong for an inline disclosure.
 */
export default function SearchPanelTrigger({
    open,
    onToggle,
    panelId,
    labelGate,
}: {
    open: boolean;
    onToggle: () => void;
    /** id of the panel this trigger discloses, for `aria-controls`. */
    panelId: string;
    /**
     * Visibility gate for the WORD beside the glyph — pass the brand's
     * `desktopInline`.
     *
     * The trigger itself is present at every width (bg-home-m-375:80 through
     * bg-home-d-1280:75); only the word steps. Live BG's mobile trigger is
     * icon-only — the `<i>` magnifier with no text sibling at 375, 440 and 1024
     * — and the word returns at 1025, NOT at 1280 as this note used to say. The
     * span is always in live's markup and is hidden by class: its `desktop-only`
     * resolves to `display:none` only below 1025, in live's own shipped
     * stylesheet (see the glyph-box note at the render below for the citation).
     * The 1280 reading came from capture widths, which skip 1025-1279 entirely.
     * BG's `desktopInline` already gates on `bgnav:`, so the value passed here
     * was right while the reason written beside it was wrong. Omit to keep the
     * word at all widths.
     */
    labelGate?: string;
}): ReactElement {
    const { t } = useTranslation('header');
    const label = t('openSearchPanel', { defaultValue: 'Search' });

    return (
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={panelId}
            // The accessible name has to live on the button, not on the span:
            // once `labelGate` hides the word at mobile the button would
            // otherwise have no name at all. Live BG names its own trigger the
            // same way (`aria-label="Open search panel"`).
            aria-label={label}
            data-testid="header-search-panel-trigger"
            // No box padding and no framework button chrome: live's trigger is a
            // bare flex row of glyph + word (bg-home-d-1280/App.tsx:75). The
            // glyph sits BEFORE the word, matching that DOM order.
            // `focus-ring-button` is the shared §7.2 focus ring every other raw
            // control in this header uses.
            // `bgnav:gap-0` because at 1025+ the spacing stops being a flex gap and
            // becomes live's own two contributions — the glyph's `margin:4px` and the
            // word's `padding-left:4px`. Those sum to the SAME 8px of ink-to-ink space
            // the `gap-2` was already producing, so this is a redistribution, not a
            // widening; what it buys is live's 4px of margin on the glyph's LEFT, which
            // a gap cannot express and which is the whole of the leftward offset the
            // owner reported. Below 1025 nothing changes: `gap-2` still applies, the
            // word is hidden by `labelGate` anyway, and the mobile glyph is untouched.
            className="flex items-center gap-2 bgnav:gap-0 cursor-pointer focus-ring-button hover:opacity-50 transition-opacity">
            {/* GLYPH BOX, AND IT STEPS AT 1025 — the same boundary the rest of
                this brand's header flips at, not a boundary of its own.

                THIS LINE USED TO STEP AT 1280 AND THAT WAS WRONG. The owner
                reported it: at 1025 our header converts to the text-links arm
                with smaller lettering and keeps a 27px magnifier beside it,
                where live shrinks the magnifier in the same breath. Measured on
                the running app, brand `bg`, guards asserting href, clientWidth
                and data-brand inside the evaluate — svg box by width:
                1024 27x27 (word hidden) | 1025 27x27 (word VISIBLE) |
                1279 27x27 | 1280 16x16. Live's is 30x30 at 1024 and 14x14 at
                1025, so the band 1025-1279 carried an 11px-oversized glyph.

                LIVE'S OWN SHIPPED STYLESHEET SETTLES THE BOUNDARY, and it is
                the source the earlier reading never consulted. The magnifier is
                the `<i class="search-button__icon">` inside live's header
                trigger, the sibling immediately before the "Search" word span
                (dumps bg-plp-d-1280, dt-plp-1280-bg.html; the whole element is
                `div.search-button[role=button][aria-label="Open search panel"]`
                holding that `<i>` then `span.search-button__text desktop-only`).
                In client.d19794a33980fdba6d07.bundle.css — the hash live's own
                stylesheet link names — at offsets 408496 and 408082, control
                `.search-button` 9 occurrences in that file:

                    .search-button__icon{ … height:14px; margin:4px; width:14px}
                    @media screen and (max-width:1024px){
                        .search-button__icon{height:30px;margin:0;
                          position:absolute;width:30px}}
                    @media (min-width:1025px){
                        .search-button__icon{background-image:url(…14x14 svg…)}}

                So the BOX steps 30 -> 14 at 1025 and the DRAWING swaps there
                too — live carries two different magnifiers, a 30x30 svg with
                r=8.193 at stroke-width 1.6 below, and a 14x14 svg with r=4.78 at
                stroke-width 1.2 above. The WORD steps at the identical width:
                its `desktop-only` class is
                `@media (max-width:1024px){.desktop-only{display:none}}` in
                the same bundle. Icon and letters step together, which is exactly
                what the owner described.

                WHY THE 1280 READING SURVIVED. It was taken from capture widths
                rather than from live's CSS: the decompiled frames run 375, 440,
                768, 1024, 1280, 1440, so the smallest one showing the 14x14 `<i>`
                is 1280. Those captures bound the step to somewhere in
                (1024, 1280] and can say nothing finer — there is no capture
                between. The comment recorded the bound as the value.

                BOX 30 IS NOT THE NUMBER TO SHIP, because the two glyphs fill
                their boxes differently and INK is what a reader sees. Live's
                base64 `<i>` decodes to a 30x30 svg whose circle is r=8.193 at
                cx=12.993 with stroke-width 1.6 and a handle ending at 25.727 —
                an ink bbox of 22.53 x 22.16. Read independently off the owner's
                live capture (.reference/live-shots/bg-search-390.png, address
                bar `bergdorfgoodman.com`, DPR 2 from a 780px page strip): ink
                22.5 x 22.0. Two agreeing sources.

                lucide's magnifier inks 2..22 of its 24 viewBox in both axes, so
                ink = 0.8333 x box, and 22.5 / 0.8333 = 27. Confirmed in-browser
                at clientWidth 390, brand `bg`: box 27 x 27, ink 22.5 wide.

                THE DESKTOP BOX STAYS AT 16 AND ONLY ITS BREAKPOINT MOVES, and
                that is a deliberate ink decision rather than a box one, by the
                same arithmetic as the mobile tier above. Live's desktop drawing
                inks 13.46 x 13.19 inside its 14x14 box: the circle is r=4.78 at
                cx=cy=5.38 and the handle ends at (12.856, 12.586), with
                stroke-width 1.2 adding 0.6 of half-stroke on every side. lucide
                inks 0.8333 of its box, so 16px paints 13.33 — 0.13px under
                live's width and 0.14 over its height. Shipping live's literal 14
                would paint 11.67 and read visibly small. Box 16, ink live's.

                `bgnav:` RATHER THAN A BUILT-IN BREAKPOINT, and it has to be:
                `--breakpoint-bgnav` is declared in our own theme block, and
                Tailwind emits that variant group BEFORE sm/md/lg/xl rather than
                in pixel order, so a `bgnav:` utility loses to any built-in
                breakpoint utility setting the same property. It beats an
                UNPREFIXED one, which is all this pair needs — `size-` appears on
                this element exactly twice and the base arm carries no variant.
                See the `cartBoxPadX` note in lib/brand/header-composition.ts for
                the rule indices this was read off. */}
            {/* MOBILE ARM — NOW LIVE'S OWN DRAWING TOO. This block used to say the
                mobile tier was deliberately unchanged, because the report that produced
                the desktop arm below was explicitly scoped to "every desktop width
                (1025+)". That scope has now been lifted by a report of the SAME two
                defects on mobile. Owner, 2026-08-23, on live BG at 390:
                *"our search icon is too bold and sits high and left"* and *"live's
                magnifying glass handle doesn't contact the round glass part"* — which
                is, word for word, the pair the desktop arm was rebuilt to fix.

                LIVE SERVES A DIFFERENT MAGNIFIER BELOW 1025, so this is not the 14x14
                glyph re-used at another size. Decoded from BG's own shipped bundle
                (dumps/bg-plp-d-1280/dt-plp-1280-bg_files/
                client.d19794a33980fdba6d07.bundle.css; 9 `.search-button` occurrences
                by `grep -o | wc -l`, positive control 9,554 rule blocks in the file —
                note `grep -c` reports 1 there, because the bundle is one long line).
                The UNMEDIATED `.search-button__icon` background-image is a 30x30 svg,
                and `@media (min-width:1025px)` swaps in the 14x14 one the arm below
                already ships. The box steps with it: `@media screen and
                (max-width:1024px){.search-button__icon{height:30px;width:30px;margin:0;
                position:absolute}}`, and at `max-width:767px` it is placed at
                `left:7px;top:7px`. So 30 is live's own mobile box, not a derived one.

                THE HANDLE IS A SEPARATE PATH AND IT DOES NOT TOUCH THE CIRCLE. The
                circle is r=8.193 centred (12.993, 12.993), so its edge on the 45°
                diagonal is 12.993 + 8.193 x 0.70711 = 18.786. The handle's moveto is
                21.615. That is 2.829 units of clear space on each axis — 4.001 along
                the diagonal, less 0.8 of half-stroke at each end, so 2.40 units of
                VISIBLE gap, which paints ~2.4 CSS px at this 1:1 box. lucide's
                magnifier puts its circle edge at 16.657 and its handle moveto at 16.7:
                a 0.043-unit overlap, i.e. joined. No scaling of a joined glyph produces
                a gap, which is why this had to be a drawing swap rather than a resize.

                AND THE SWAP IS ALSO THE BOLDNESS FIX. Ink mass measured through one
                instrument on both sides (canvas decode, DPR 2, ink coverage summed and
                DPR-normalised): live 90.70 against our lucide 140.82 — a ratio of
                1.553. The control for that ratio is the two glyphs in this same header
                that measure byte-identical in size on both sides, the bag (21.5 x 25.5)
                and the hamburger (26.5 x 15.0); they ink at 1.022 and 1.045. So the
                systematic rasterisation difference between his capture and our render
                is 2-5%, and 55% is a real stroke difference: lucide's stroke-width 2 in
                a 24 viewBox paints 2.25 CSS px at box 27, against live's 1.6 at box 30.

                `currentColor` rather than live's literal `#000`, for the same reason
                the desktop arm gives: the glyph must dim with `hover:opacity-50`. */}
            <svg
                className="bgnav:hidden size-[30px] shrink-0"
                viewBox="0 0 30 30"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true">
                <path d="M21.186 12.993a8.193 8.193 0 1 1-16.386 0 8.193 8.193 0 1 1 16.386 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="m21.615 21.246 4.112 4.112" />
            </svg>
            {/* DESKTOP ARM — LIVE'S OWN GLYPH, AT LIVE'S OWN BOX. This retires the
                ink-matching compensation the block above describes ("Box 16, ink live's"),
                and it retires it because the owner's flicker test refuted it:
                *"our icon is a little too big, and a little low and left … live icon at all
                widths actually doesn't have contact between the handle of the magnifying
                glass and the circle."*

                He is right, and the second half is why the first half could not be fixed by
                resizing. THE TWO GLYPHS ARE DIFFERENT DRAWINGS. Live's handle is DETACHED
                from the circle; lucide's is not. Decoded from the `background-image` on
                `.search-button__icon` inside `@media (min-width:1025px)` in BG's own shipped
                bundle (live-shots/owner-2026-08-22/bg-account-dt_files/
                client.13d04758ed47cf397a03.bundle.css), which is the same declaration the
                block above already cites by a different hash:

                    <svg width="14" height="14"><path fill="none" stroke="#000"
                      stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2"
                      d="M10.16 5.38a4.78 4.78 0 1 1-9.56 0 4.78 4.78 0 0 1 9.56 0z
                         m.28 4.79 2.416 2.416"/></svg>

                The circle is r=4.78 at (5.38,5.38), so its edge on the 45° diagonal is at
                5.38 + 4.78·√2/2 = 8.760. The handle's `moveto` is 10.44. That 1.68-unit gap
                on each axis IS the detachment, and no scaling of a joined glyph produces it.

                So the arithmetic that chose 16 is now moot: with live's actual drawing the
                box and the ink agree by construction, and the honest box is live's 14.
                Matching ink by oversizing a different shape was the right instinct against
                the evidence then available and the wrong answer — it fixed the number the
                measurement could see and left the shape it could not.

                `m-1` is live's `.search-button__icon{margin:4px}` from the unmediated rule
                in the same file. It applies here only because this arm is desktop-only.
                `currentColor` rather than live's literal `#000`: the glyph must dim with the
                trigger's `hover:opacity-50`, and BG's header ink is already black — a
                divergence in the declaration, not in the paint. */}
            <svg
                className="hidden bgnav:block size-[14px] m-1 shrink-0"
                viewBox="0 0 14 14"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.2"
                strokeLinecap="round"
                strokeLinejoin="round"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true">
                <path d="M10.16 5.38a4.78 4.78 0 1 1-9.56 0 4.78 4.78 0 0 1 9.56 0zm.28 4.79 2.416 2.416" />
            </svg>
            {/* `.search-button__text{padding-top:2px;padding-left:4px;padding-right:4px}`,
                same file, no media query — so it is live's value at every width, and the
                word is hidden below 1025 by `labelGate` regardless. The 2px is the whole of
                *"our text … is just too far up"*; the 4px left is the other half of the
                8px ink gap described on the button above. Type is NOT touched: the owner
                called our word *"correct size and look"*, and live's 13px already matches. */}
            <span className={`${labelGate ?? ''} pt-[2px] px-1`.trimStart()}>{label}</span>
        </button>
    );
}
